/**
 * Deal Structure Comparison Component
 *
 * Compares asset sale vs stock sale including:
 * - Tax implications
 * - Net proceeds
 * - Benefits and drawbacks
 */

import React from 'react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

function DealStructureComparison({ dealStructureAnalysis }) {
  if (!dealStructureAnalysis) return null;

  const { assetSale, stockSale, comparison } = dealStructureAnalysis;

  const isStockRecommended = comparison.recommendation === 'stock_sale';

  return (
    <div className="deal-structure-comparison">
      <h2>Deal Structure Comparison</h2>
      <p className="section-description">
        Compare the tax implications and net proceeds of asset sale vs. stock sale structures
      </p>

      {/* Recommendation Banner */}
      <div className={`recommendation-banner ${isStockRecommended ? 'stock' : 'asset'}`}>
        <div className="banner-content">
          <span className="recommended-label">Recommended Structure</span>
          <h3>{isStockRecommended ? 'Stock Sale' : 'Asset Sale'}</h3>
          <p>{comparison.recommendationReason}</p>
        </div>
        <div className="banner-savings">
          <span className="savings-label">Tax Savings</span>
          <span className="savings-value">{formatCurrency(comparison.taxSavings)}</span>
        </div>
      </div>

      {/* Side by Side Comparison */}
      <div className="structures-grid">
        {/* Asset Sale Card */}
        <div className={`structure-card asset ${!isStockRecommended ? 'recommended' : ''}`}>
          <div className="card-header">
            <h3>{assetSale.name}</h3>
            {!isStockRecommended && <span className="badge">Recommended</span>}
          </div>
          <p className="structure-description">{assetSale.description}</p>

          <div className="tax-breakdown">
            <h4>Tax Analysis</h4>
            <div className="tax-row">
              <span>Gain</span>
              <span>{formatCurrency(assetSale.taxAnalysis.gain)}</span>
            </div>
            {Object.entries(assetSale.taxAnalysis.taxes || {}).map(([taxType, amount]) => (
              <div className="tax-row" key={taxType}>
                <span>{formatTaxType(taxType)}</span>
                <span className="negative">-{formatCurrency(amount)}</span>
              </div>
            ))}
            <div className="tax-row total">
              <span>Total Tax</span>
              <span className="negative">-{formatCurrency(assetSale.taxAnalysis.totalTax)}</span>
            </div>
            <div className="tax-row effective">
              <span>Effective Rate</span>
              <span>{assetSale.taxAnalysis.effectiveRate}%</span>
            </div>
            <div className="tax-row net">
              <span>Net Proceeds</span>
              <span className="highlight">{formatCurrency(assetSale.taxAnalysis.netProceeds)}</span>
            </div>
          </div>

          <div className="pros-cons">
            <div className="pros">
              <h4>Seller Benefits</h4>
              <ul>
                {assetSale.sellerBenefits?.map((benefit, idx) => (
                  <li key={idx}>{benefit}</li>
                ))}
              </ul>
            </div>
            <div className="cons">
              <h4>Seller Drawbacks</h4>
              <ul>
                {assetSale.sellerDrawbacks?.map((drawback, idx) => (
                  <li key={idx}>{drawback}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Stock Sale Card */}
        <div className={`structure-card stock ${isStockRecommended ? 'recommended' : ''}`}>
          <div className="card-header">
            <h3>{stockSale.name}</h3>
            {isStockRecommended && <span className="badge">Recommended</span>}
          </div>
          <p className="structure-description">{stockSale.description}</p>

          <div className="tax-breakdown">
            <h4>Tax Analysis</h4>
            <div className="tax-row">
              <span>Gain</span>
              <span>{formatCurrency(stockSale.taxAnalysis.gain)}</span>
            </div>
            {stockSale.taxAnalysis.qsbsExclusion > 0 && (
              <div className="tax-row qsbs">
                <span>QSBS Exclusion</span>
                <span className="positive">-{formatCurrency(stockSale.taxAnalysis.qsbsExclusion)}</span>
              </div>
            )}
            {Object.entries(stockSale.taxAnalysis.taxes || {}).map(([taxType, amount]) => (
              <div className="tax-row" key={taxType}>
                <span>{formatTaxType(taxType)}</span>
                <span className="negative">-{formatCurrency(amount)}</span>
              </div>
            ))}
            <div className="tax-row total">
              <span>Total Tax</span>
              <span className="negative">-{formatCurrency(stockSale.taxAnalysis.totalTax)}</span>
            </div>
            <div className="tax-row effective">
              <span>Effective Rate</span>
              <span>{stockSale.taxAnalysis.effectiveRate}%</span>
            </div>
            <div className="tax-row net">
              <span>Net Proceeds</span>
              <span className="highlight">{formatCurrency(stockSale.taxAnalysis.netProceeds)}</span>
            </div>
          </div>

          <div className="pros-cons">
            <div className="pros">
              <h4>Seller Benefits</h4>
              <ul>
                {stockSale.sellerBenefits?.map((benefit, idx) => (
                  <li key={idx}>{benefit}</li>
                ))}
              </ul>
            </div>
            <div className="cons">
              <h4>Seller Drawbacks</h4>
              <ul>
                {stockSale.sellerDrawbacks?.map((drawback, idx) => (
                  <li key={idx}>{drawback}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Summary */}
      <div className="comparison-summary">
        <h4>Net Proceeds Comparison</h4>
        <div className="comparison-bar">
          <div className="bar asset" style={{ width: `${(assetSale.taxAnalysis.netProceeds / Math.max(assetSale.taxAnalysis.netProceeds, stockSale.taxAnalysis.netProceeds)) * 100}%` }}>
            <span className="bar-label">Asset Sale</span>
            <span className="bar-value">{formatCurrency(assetSale.taxAnalysis.netProceeds)}</span>
          </div>
          <div className="bar stock" style={{ width: `${(stockSale.taxAnalysis.netProceeds / Math.max(assetSale.taxAnalysis.netProceeds, stockSale.taxAnalysis.netProceeds)) * 100}%` }}>
            <span className="bar-label">Stock Sale</span>
            <span className="bar-value">{formatCurrency(stockSale.taxAnalysis.netProceeds)}</span>
          </div>
        </div>
        <p className="difference">
          Difference: <strong>{formatCurrency(Math.abs(comparison.netProceedsDifference))}</strong>
          {' '}({Math.abs(comparison.percentDifference)}% {comparison.netProceedsDifference > 0 ? 'higher' : 'lower'} with stock sale)
        </p>
      </div>
    </div>
  );
}

function formatTaxType(type) {
  const types = {
    corporateTax: 'Corporate Tax',
    dividendTax: 'Dividend Tax',
    niit: 'NIIT (3.8%)',
    stateTax: 'State Tax',
    ordinaryIncomeTax: 'Ordinary Income Tax',
    capitalGainsTax: 'Capital Gains Tax'
  };
  return types[type] || type;
}

export default DealStructureComparison;
